<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import { pageById } from '@/data/registry'
import { usePrototypeStore } from '@/stores/prototype'
import { useUiStore } from '@/stores/ui'
import ProTablePage from '@/components/pro/ProTablePage.vue'
import EChart from '@/components/ui/EChart.vue'
import AppIcon from '@/components/AppIcon.vue'
import { formatNumber } from '@/utils/format'

const definition = pageById('drugs')
if (!definition) throw new Error('药品资料页面定义缺失')

const store = usePrototypeStore()
const router = useRouter()
const ui = useUiStore()
const db = store.db
const tableRef = ref<InstanceType<typeof ProTablePage> | null>(null)

const drugs = computed(() => db.drugs)
const featured = computed(() => [...drugs.value].sort((left, right) => String(right.updated_at ?? '').localeCompare(String(left.updated_at ?? '')))[0])

const categoryRows = computed(() => {
  const map = new Map<string, number>()
  for (const drug of drugs.value) {
    const key = String(drug.category_name ?? '其他')
    map.set(key, (map.get(key) ?? 0) + 1)
  }
  return [...map.entries()].sort((left, right) => right[1] - left[1]).map(([name, value], index) => ({
    name,
    value,
    percent: drugs.value.length ? Math.round((value / drugs.value.length) * 1000) / 10 : 0,
    color: ['#08b578', '#3b82f6', '#f5a623', '#ef6f8f', '#8b7cf6', '#22b8cf', '#9aa8bd'][index % 7],
  }))
})

const categoryOption = computed<EChartsOption>(() => ({
  animationDuration: 480,
  tooltip: { trigger: 'item', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
  series: [{
    type: 'pie', radius: ['58%', '80%'], center: ['50%', '50%'], label: { show: false }, avoidLabelOverlap: true,
    itemStyle: { borderColor: '#fff', borderWidth: 2 },
    data: categoryRows.value.map(item => ({ name: item.name, value: item.value, itemStyle: { color: item.color } })),
  }],
  graphic: [
    { type: 'text', left: 'center', top: '41%', style: { text: formatNumber(drugs.value.length), fill: '#0b1b3a', fontSize: 22, fontWeight: 700, fontFamily: 'DIN Alternate, sans-serif' } },
    { type: 'text', left: 'center', top: '55%', style: { text: '药品总数', fill: '#7183a1', fontSize: 9 } },
  ],
}))

const lowStock = computed(() => [...drugs.value]
  .filter(drug => Number(drug.stock ?? 0) < Number(drug.safety_stock ?? 0))
  .sort((left, right) => (Number(left.stock) / Math.max(1, Number(left.safety_stock))) - (Number(right.stock) / Math.max(1, Number(right.safety_stock))))
  .slice(0, 4))

function go(path: string): void {
  void router.push(path)
}

async function copyBarcode(): Promise<void> {
  const barcode = String(featured.value?.barcode ?? '')
  if (!barcode) return
  try {
    await navigator.clipboard.writeText(barcode)
    ui.toast('success', '条形码已复制', barcode)
  } catch {
    ui.toast('info', '条形码', barcode)
  }
}

function editFeatured(): void {
  if (!featured.value) return
  const target = tableRef.value as unknown as { openEdit?: (row: Record<string, unknown>) => void } | null
  if (target?.openEdit) target.openEdit(featured.value)
  else go('/drugs')
}
</script>

<template>
  <ProTablePage ref="tableRef" :definition="definition">
    <template #after-table>
      <section class="drug-panels" aria-label="药品结构与库存预警">
        <article class="card drug-panel">
          <header class="card__head">
            <div><h2 class="card__title">药品分类分布</h2><p class="card__sub">按药品主数据分类统计</p></div>
            <button type="button" class="card__link" @click="go('/drugs?panel=categories')">查看全部 <AppIcon name="chevron-right" :size="12" /></button>
          </header>
          <div class="drug-category">
            <EChart :option="categoryOption" height="142px" aria-label="药品分类分布环图" />
            <div class="drug-category__legend">
              <span v-for="item in categoryRows.slice(0, 6)" :key="item.name"><i :style="{ background: item.color }" />{{ item.name }}<b>{{ item.percent }}%</b></span>
            </div>
          </div>
        </article>

        <article class="card drug-panel">
          <header class="card__head">
            <div><h2 class="card__title">低库存预警 <span class="tag tag--danger">{{ lowStock.length }}</span></h2><p class="card__sub">低于安全库存，建议及时补货</p></div>
            <button type="button" class="card__link" @click="go('/drugs?stock_state=low')">查看全部 <AppIcon name="chevron-right" :size="12" /></button>
          </header>
          <div class="low-stock">
            <button v-for="drug in lowStock" :key="String(drug.id)" type="button" class="low-stock__row" @click="go('/drugs')">
              <span class="drug-thumb">{{ String(drug.generic_name).slice(0, 1) }}</span>
              <span class="low-stock__main"><b>{{ drug.generic_name }}</b><small>{{ drug.spec }} · 安全库存 {{ drug.safety_stock }} {{ drug.unit }}</small></span>
              <span class="low-stock__count"><b>{{ drug.stock }}</b><small>剩余 {{ drug.unit }}</small></span>
            </button>
            <div v-if="!lowStock.length" class="empty-state" style="min-height: 140px"><h3 class="empty-state__title">库存均高于安全线</h3><p class="empty-state__text">低于安全库存的药品会显示在这里。</p></div>
          </div>
        </article>

        <article v-if="featured" class="card drug-panel drug-panel--detail">
          <header class="card__head">
            <div><h2 class="card__title">药品详情</h2><p class="card__sub">最近更新的药品主数据</p></div>
            <button type="button" class="btn btn--sm" @click="editFeatured"><AppIcon name="edit" :size="13" />编辑</button>
          </header>
          <div class="drug-detail">
            <div class="drug-detail__head">
              <span class="drug-detail__image">{{ String(featured.generic_name).slice(0, 1) }}</span>
              <div class="drug-detail__title">
                <div><b>{{ featured.generic_name }}</b><span class="tag tag--info tag--plain">{{ featured.otc_type }}</span></div>
                <small>{{ featured.trade_name }} · {{ featured.approval_no }}</small>
                <small>{{ featured.spec }} · {{ featured.manufacturer }}</small>
              </div>
            </div>
            <dl class="drug-detail__meta">
              <div><dt>适应症</dt><dd>{{ featured.indication }}</dd></div>
              <div><dt>用法用量</dt><dd>{{ featured.usage }}</dd></div>
              <div><dt>注意事项</dt><dd>{{ featured.precautions || '按说明书使用，注意禁忌人群。' }}</dd></div>
            </dl>
            <div class="drug-detail__foot">
              <span>条形码 {{ featured.barcode }}</span>
              <button type="button" class="text-button" @click="copyBarcode">复制</button>
            </div>
          </div>
        </article>
      </section>
    </template>
  </ProTablePage>
</template>

<style scoped>
.drug-panels { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr) minmax(0, 1.2fr); gap: 14px; margin-top: 14px; }
.drug-panel { min-width: 0; overflow: hidden; padding-bottom: 10px; }
.drug-category { display: grid; grid-template-columns: 142px minmax(0, 1fr); align-items: center; gap: 4px; padding: 0 10px 8px 0; }
.drug-category__legend { display: grid; gap: 5px; }
.drug-category__legend span { display: flex; align-items: center; gap: 5px; font-size: 9.5px; color: var(--c-muted); }
.drug-category__legend i { width: 6px; height: 6px; border-radius: 50%; flex: 0 0 auto; }
.drug-category__legend b { margin-left: auto; font-family: var(--font-num); font-size: 9.5px; color: var(--c-text); }
.low-stock { padding: 0 16px 4px; }
.low-stock__row { width: 100%; display: flex; align-items: center; gap: 8px; padding: 7px 0; border: 0; border-top: 1px solid var(--c-divider); background: transparent; text-align: left; }
.low-stock__row:first-child { border-top: 0; }
.drug-thumb { width: 30px; height: 30px; flex: 0 0 auto; border-radius: 7px; display: grid; place-items: center; background: #eef6ff; color: #2f7dcc; font-size: 11px; font-weight: 700; border: 1px solid #dbeafb; }
.low-stock__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.low-stock__main b { font-size: 10px; color: var(--c-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.low-stock__main small { margin-top: 2px; font-size: 8.5px; color: var(--c-faint); }
.low-stock__count { display: flex; flex-direction: column; align-items: flex-end; }
.low-stock__count b { font-family: var(--font-num); font-size: 13px; color: var(--c-danger); line-height: 1.1; }
.low-stock__count small { font-size: 8px; color: var(--c-faint); }
.drug-panel--detail { padding-bottom: 12px; }
.drug-detail { padding: 0 16px; }
.drug-detail__head { display: flex; gap: 10px; align-items: flex-start; }
.drug-detail__image { width: 54px; height: 54px; border-radius: 9px; display: grid; place-items: center; background: linear-gradient(145deg, #e7f8f1, #c9efe1); color: var(--c-brand-strong); font-size: 17px; font-weight: 700; border: 1px solid #cdecdf; flex: 0 0 auto; }
.drug-detail__title { min-width: 0; }
.drug-detail__title > div { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.drug-detail__title b { font-size: 12.5px; color: var(--c-ink); }
.drug-detail__title small { display: block; margin-top: 3px; font-size: 8.5px; color: var(--c-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 280px; }
.drug-detail__meta { display: grid; gap: 5px; margin: 9px 0; padding-top: 8px; border-top: 1px solid var(--c-divider); }
.drug-detail__meta div { display: flex; gap: 8px; font-size: 9px; line-height: 1.5; }
.drug-detail__meta dt { flex: 0 0 50px; color: var(--c-faint); }
.drug-detail__meta dd { margin: 0; color: var(--c-text); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.drug-detail__foot { display: flex; align-items: center; gap: 8px; padding-top: 7px; border-top: 1px solid var(--c-divider); font-size: 9px; color: var(--c-faint); }
.drug-detail__foot .text-button { margin-left: auto; }
@media (max-width: 1399px) { .drug-panels { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1023px) { .drug-panels { grid-template-columns: minmax(0, 1fr); } }
</style>
